import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { trackLogin } from '../utils/analytics';
import safeStorage from '../utils/safeStorage';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('email');
  const [error, setError] = useState('');
  const [noAccount, setNoAccount] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const { sendLoginOtp, verifyOtp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const wasKicked = new URLSearchParams(location.search).get('kicked') === '1';
  // Honour ?redirect= (set by PrivateRoute) / ?next= so deep links survive login
  const searchP = new URLSearchParams(location.search);
  const redirectTo = searchP.get('redirect') || searchP.get('next') || null;

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError(''); setNoAccount(false); setLoading(true);
    try {
      const result = await sendLoginOtp(email);
      if (result.sent === false) {
        setNoAccount(true);
        return;
      }
      setStep('otp');
      setCooldown(60);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP. Please try again.');
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await verifyOtp(email, otp);
      trackLogin('email');
      const pendingToken = safeStorage.getItem('pendingGiftToken');
      if (pendingToken) {
        safeStorage.removeItem('pendingGiftToken');
        navigate(`/gift/redeem/${pendingToken}`);
      } else if (redirectTo && redirectTo.startsWith('/') && !redirectTo.startsWith('//')) {
        navigate(redirectTo);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      if (err.response?.data?.maintenance) {
        navigate('/maintenance');
        return;
      }
      if (err.response?.data?.suspended) {
        const reason = encodeURIComponent(err.response.data?.message || '');
        navigate(`/suspended?reason=${reason}`);
        return;
      }
      setError(err.response?.data?.message || 'Invalid or expired OTP.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-vh-100 d-flex" style={{background:'linear-gradient(135deg,#1e1048 0%,#2d1b69 60%,#4a2a9e 100%)'}}>
      <Helmet>
        <title>Login | Dreamy Bedtime</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Left panel — brand & benefits */}
      <div className="d-none d-lg-flex flex-column justify-content-center p-5 col-lg-6"
        style={{position:'relative', overflow:'hidden'}}>
        {/* Decorative circles */}
        <div style={{position:'absolute', top:'-80px', left:'-80px', width:'300px', height:'300px', borderRadius:'50%', background:'rgba(255,255,255,0.04)'}}></div>
        <div style={{position:'absolute', bottom:'-60px', right:'-60px', width:'250px', height:'250px', borderRadius:'50%', background:'rgba(255,255,255,0.04)'}}></div>

        <Link to="/" className="text-decoration-none d-block mb-5">
          <span style={{fontFamily:"'Fredoka One',cursive", fontSize:'36px', color:'white', letterSpacing:'0.5px'}}>
            🌙 Dreamy Bedtime
          </span>
        </Link>

        <h2 className="fw-bold mb-2" style={{color:'white', fontFamily:"'Fredoka One',cursive", fontSize:'36px', lineHeight:'1.2'}}>
          Welcome back,<br />storyteller
        </h2>
        <p style={{color:'rgba(255,255,255,0.65)', fontSize:'16px', marginBottom:'40px'}}>
          Your children are waiting for tonight's story.
        </p>

        <div className="d-flex flex-column gap-3 mb-5">
          {[
            ['✨', 'Unique story every time', 'Every story is completely unique'],
            ['🎨', 'Magical illustrations included', 'Beautiful artwork generated automatically'],
            ['🌍', '11 languages supported', 'Perfect for bilingual families'],
            ['📅', 'Themed weekly schedules', 'Monday = Courage, Tuesday = Friendship…'],
          ].map(([icon, title, desc]) => (
            <div key={title} className="d-flex align-items-start gap-3">
              <div className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                style={{width:'44px', height:'44px', background:'rgba(255,255,255,0.12)', fontSize:'20px'}}>
                {icon}
              </div>
              <div>
                <div className="fw-semibold" style={{color:'white', fontSize:'14px'}}>{title}</div>
                <div style={{color:'rgba(255,255,255,0.5)', fontSize:'13px'}}>{desc}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="p-3 rounded-3" style={{background:'rgba(255,255,255,0.08)'}}>
          <div className="d-flex align-items-center gap-2 mb-1">
            <span style={{fontSize:'20px'}}>⭐⭐⭐⭐⭐</span>
          </div>
          <p className="mb-1 fst-italic" style={{color:'rgba(255,255,255,0.8)', fontSize:'14px'}}>
            "My daughter now asks for a Dreamy Bedtime story every single night. It's become our favourite ritual."
          </p>
          <div style={{color:'rgba(255,255,255,0.5)', fontSize:'12px'}}>— Sarah M., mum of two</div>
        </div>
      </div>

      {/* Right panel — form */}
      {/* --card-bg, not white: the form's text uses --text-main, which is pale
          lavender in dark mode — on a hardcoded white panel the whole sign-in
          form was unreadable. */}
      <div className="d-flex align-items-center justify-content-center p-4 p-lg-5 flex-grow-1"
        style={{background:'var(--card-bg)', borderRadius:'0', minHeight:'100vh'}}>
        <div style={{width:'100%', maxWidth:'400px'}}>

          {/* Mobile logo */}
          <div className="d-lg-none text-center mb-4">
            <Link to="/" className="text-decoration-none">
              <span style={{fontFamily:"'Fredoka One',cursive", fontSize:'28px', color:'var(--text-main)'}}>🌙 Dreamy Bedtime</span>
            </Link>
          </div>

          <h3 className="fw-bold mb-1" style={{color:'var(--text-main)', fontFamily:"'Fredoka One',cursive", fontSize:'28px'}}>
            {step === 'email' ? 'Sign In' : 'Check your email'}
          </h3>
          <p className="text-muted small mb-4">
            {step === 'email'
              ? 'No password needed — we\'ll email you a secure code.'
              : <>We sent a 6-digit code to <strong>{email}</strong>.</>}
          </p>

          {wasKicked && (
            <div className="alert py-2 small rounded-3 d-flex align-items-center gap-2 mb-3"
              style={{background:'#fff3cd', border:'1px solid #ffc107', color:'#7d5a00'}}>
              <i className="bi bi-info-circle-fill"></i>
              You were signed out. This can happen when your account is signed in on too many devices.
            </div>
          )}

          {error && (
            <div className="alert alert-danger py-2 small rounded-3 d-flex align-items-center gap-2">
              <i className="bi bi-exclamation-circle-fill"></i>{error}
            </div>
          )}

          {noAccount && (
            <div className="alert py-2 small rounded-3 d-flex align-items-center gap-2 mb-3"
              style={{background:'var(--yellow-section)', border:'1px solid var(--border-yellow)', color:'var(--text-orange2)'}}>
              <i className="bi bi-person-x-fill"></i>
              <span>No account found for this email.{' '}
                <Link to={`/register?email=${encodeURIComponent(email)}${redirectTo ? `&redirect=${encodeURIComponent(redirectTo)}` : ''}`} style={{color:'#6f42c1', fontWeight:700}}>
                  Sign up free →
                </Link>
              </span>
            </div>
          )}

          {step === 'email' ? (
            <form onSubmit={handleSendOtp}>
              <div className="mb-4">
                <label className="form-label fw-semibold small" style={{color:'var(--text-main)'}}>Email Address</label>
                <input type="email" className="form-control form-control-lg" placeholder="you@example.com"
                  style={{borderRadius:'12px'}}
                  value={email} onChange={e => { setEmail(e.target.value); setNoAccount(false); setError(''); }} required />
              </div>
              <button type="submit" className="btn btn-dream btn-lg w-100 rounded-pill fw-bold" disabled={loading}>
                {loading
                  ? <><span className="spinner-border spinner-border-sm me-2"></span>Sending code…</>
                  : <><i className="bi bi-envelope me-2"></i>Send Secure Code</>}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp}>
              <div className="mb-4">
                <label className="form-label fw-semibold small" style={{color:'var(--text-main)'}}>Verification Code</label>
                <input type="text" className="form-control form-control-lg text-center fw-bold"
                  placeholder="000 000" maxLength={6} inputMode="numeric"
                  style={{borderRadius:'12px', letterSpacing:'6px', fontSize:'24px'}}
                  value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ''))} required />
                <div className="d-flex justify-content-between mt-2">
                  <span className="small text-muted">Check your spam folder too</span>
                  <button type="button" className="btn btn-link btn-sm p-0"
                    style={{color: cooldown > 0 ? '#9ca3af' : '#6f42c1', fontSize:'13px'}}
                    onClick={handleSendOtp} disabled={loading || cooldown > 0}>
                    {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
                  </button>
                </div>
              </div>
              <button type="submit" className="btn btn-dream btn-lg w-100 rounded-pill fw-bold mb-3" disabled={loading}>
                {loading
                  ? <><span className="spinner-border spinner-border-sm me-2"></span>Verifying…</>
                  : <><i className="bi bi-check2-circle me-2"></i>Verify & Sign In</>}
              </button>
              <button type="button" className="btn btn-outline-secondary w-100 rounded-pill"
                onClick={() => { setStep('email'); setOtp(''); setError(''); }}>
                <i className="bi bi-arrow-left me-1"></i>Use a different email
              </button>
            </form>
          )}

          <div className="text-center mt-4 pt-3" style={{borderTop:'1px solid #f0eaf8'}}>
            <span className="text-muted small">No account? </span>
            <Link to="/register" className="fw-bold text-decoration-none small" style={{color:'#6f42c1'}}>
              Start free — 5 stories/month, no credit card
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
